import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BootstrapState, SystemConfig } from '~/Types/SystemConfig';
import { LicenseService } from '~/Services/LicenseService';
import { OrganizationsService } from '~/Services/OrganizationService';
import { UserService } from '~/Services/UserService';
import { ProductService } from '~/Services/ProductService';
import { DevicesService } from '~/Services/Devices';
import { ClaimService } from '~/Services/Claim';
import { RolesService } from '~/Services/AdminRolesService';
import { AdminUserService } from '~/Services/AdminUsers';
import { StaffService } from '~/Services/StaffServices';
import { GroupServices } from '~/Services/GroupServices';
import { RoleService } from '~/Services/RoleServices';
import { ClientsService } from '~/Services/Clients';
import { ApiKeyService } from '~/Services/ApiKeyService';
import { ServicePrincipalService } from '~/Services/ServicePrincipalService';
import { PasskeyService } from '~/Services/PasskeyService';

/** Fallback khi chưa fetch xong hoặc fetch system-config.json thất bại — không phải nguồn config chính. */
export const DEFAULT_CONFIG: SystemConfig = {
  identityServerUrl: 'http://localhost:8080',
  licenseManagerUrl: 'http://localhost:8081/license_manager',
  clientId: 'admin',
  redirectUri: `${window.location.origin}/callback`,
  postLogoutRedirectUri: `${window.location.origin}/login`,
  scope: 'openid profile email roles organization_claims api offline_access',
};

/** Thiếu 1 trong các field này là lỗi cấu hình môi trường, không phải state hợp lệ. */
const REQUIRED_CONFIG_KEYS: (keyof SystemConfig)[] = [
  'identityServerUrl',
  'licenseManagerUrl',
  'clientId',
  'redirectUri',
  'postLogoutRedirectUri',
  'scope',
];

/** Trả về danh sách field bắt buộc bị thiếu/rỗng trong `config`. */
export const getMissingConfigKeys = (config: SystemConfig): string[] =>
  REQUIRED_CONFIG_KEYS.filter((key) => !config[key]);

const initialState: BootstrapState = {
  systemConfig: DEFAULT_CONFIG,
  isSuccess: false,
};

// Lưu config để services có thể truy cập
let currentConfig: SystemConfig = DEFAULT_CONFIG;

export const getCurrentConfig = (): SystemConfig => currentConfig;

const updateHostService = (config: SystemConfig) => {
  LicenseService.setConfig(config);
  OrganizationsService.setConfig(config);
  UserService.setConfig(config);
  ProductService.setConfig(config);
  DevicesService.setConfig(config);
  ClaimService.setConfig(config);
  RolesService.setConfig(config);
  AdminUserService.setConfig(config);
  StaffService.setConfig(config);
  GroupServices.setConfig(config);
  RoleService.setConfig(config);
  ClientsService.setConfig(config);
  ApiKeyService.setConfig(config);
  ServicePrincipalService.setConfig(config);
  PasskeyService.setConfig(config);
};

// Slice
const bootstrapSlice = createSlice({
  name: 'bootstrap',
  initialState,
  reducers: {
    setSystemConfig: (state, action: PayloadAction<SystemConfig>) => {
      currentConfig = action.payload;
      updateHostService(action.payload);
      state.systemConfig = action.payload;
      state.isSuccess = true;
    },
    fetchConfig: (state) => {
      state.isSuccess = false;
    },
  },
});

export const { setSystemConfig, fetchConfig } = bootstrapSlice.actions;
export const BootstrapReducer = bootstrapSlice.reducer;

